import {getUrlParam, setOrDeleteStringParam} from '@hconnect/common/utils'
import {useSearchParams} from 'react-router'

import {ALL_FREQUENCIES} from '../consts'
import {HrocJtbdRole} from '../enums'

const JobsToBeDonePageParams = {
  JTBD_ASSOCIATED_ROLE_VIEW: 'associatedRole',
  JTBD_FREQUENCY_FILTER: 'frequency',
  JTBD_DATE_RANGE_FILTER: 'dateRange'
}

const DEFAULT_JTBD_ASSOCIATED_ROLE_TYPE = undefined
const DEFAULT_JTBD_FREQUENCY = ALL_FREQUENCIES
const DEFAULT_JTBD_DATE_RANGE = 'last30days'

export const useJobsToBeDonePageState = () => {
  const jobsToBeDoneViewState = useJobsToBeDoneViewState()
  const jobsToBeDoneFrequencyState = useJobsToBeDoneFrequencyState()
  const jobsToBeDoneDateRangeState = useJobsToBeDoneDateRangeState()

  return {...jobsToBeDoneViewState, ...jobsToBeDoneFrequencyState, ...jobsToBeDoneDateRangeState}
}

const useJobsToBeDoneViewState = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const hrocRoleTypeParam = getUrlParam(
    searchParams,
    JobsToBeDonePageParams.JTBD_ASSOCIATED_ROLE_VIEW
  )
  const hrocRoleType =
    hrocRoleTypeParam && isRoleType(hrocRoleTypeParam)
      ? hrocRoleTypeParam
      : DEFAULT_JTBD_ASSOCIATED_ROLE_TYPE

  const setHrocRoleType = (value?: string) => {
    setSearchParams(
      (currentSearchParams) => {
        const newParams = new URLSearchParams(currentSearchParams)

        setOrDeleteStringParam(newParams, JobsToBeDonePageParams.JTBD_ASSOCIATED_ROLE_VIEW, value)

        // changing role should also reset the frequency filter to default, as the frequencies available are role dependent
        setOrDeleteStringParam(newParams, JobsToBeDonePageParams.JTBD_FREQUENCY_FILTER, undefined)

        return newParams
      },
      {flushSync: true}
    )
  }

  return {hrocRoleType, setHrocRoleType}
}

const useJobsToBeDoneFrequencyState = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const frequencyParam = getUrlParam(searchParams, JobsToBeDonePageParams.JTBD_FREQUENCY_FILTER)
  const selectedFrequency = frequencyParam || DEFAULT_JTBD_FREQUENCY

  const setSelectedFrequency = (value: string) => {
    setSearchParams(
      (currentSearchParams) => {
        const newParams = new URLSearchParams(currentSearchParams)

        if (value === ALL_FREQUENCIES) {
          setOrDeleteStringParam(newParams, JobsToBeDonePageParams.JTBD_FREQUENCY_FILTER, undefined)
        } else {
          setOrDeleteStringParam(newParams, JobsToBeDonePageParams.JTBD_FREQUENCY_FILTER, value)
        }

        return newParams
      },
      {flushSync: true}
    )
  }

  return {selectedFrequency, setSelectedFrequency}
}

const useJobsToBeDoneDateRangeState = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const dateRangeParam = getUrlParam(searchParams, JobsToBeDonePageParams.JTBD_DATE_RANGE_FILTER)
  const dateRange = dateRangeParam || DEFAULT_JTBD_DATE_RANGE

  const setDateRange = (value?: string) => {
    setSearchParams(
      (currentSearchParams) => {
        const newParams = new URLSearchParams(currentSearchParams)

        if (!value || value === DEFAULT_JTBD_DATE_RANGE) {
          setOrDeleteStringParam(
            newParams,
            JobsToBeDonePageParams.JTBD_DATE_RANGE_FILTER,
            undefined
          )
        } else {
          setOrDeleteStringParam(newParams, JobsToBeDonePageParams.JTBD_DATE_RANGE_FILTER, value)
        }

        return newParams
      },
      {flushSync: true}
    )
  }

  return {dateRange, setDateRange}
}

const isRoleType = (value: string): boolean => Object.keys(HrocJtbdRole).includes(value)
